import { DocsService } from '@affine/core/modules/doc';
import { WorkbenchLink } from '@affine/core/modules/workbench';
import { ToggleDownIcon } from '@blocksuite/icons/rc';
import * as Collapsible from '@radix-ui/react-collapsible';
import { useService } from '@toeverything/infra';
import { useCallback, useMemo, useState } from 'react';

import { useBranchGraph } from '../hooks/use-branch-graph';
import * as styles from './branches-inline-panel.css';

interface BranchSummary {
  branchDocId: string;
  title: string;
  createdAt: string;
}

/**
 * Inline "Branches" section that renders at the bottom of the doc
 * body — same pattern as AffineOS's bi-directional links panel and
 * the citation references the user pointed at earlier. Replaces the
 * floating right-rail card because researchers told us the overlay
 * was disconnected from the source passages and ate screen space.
 *
 * Mounted from `lit-adaper.tsx` inside `BlocksuiteDocEditor` so it
 * shares the editor's width + side-padding and only appears when a
 * doc is open. Hides itself entirely when this doc has no branches —
 * no empty heading.
 */
export function BranchesInlinePanel({ docId }: { docId: string }) {
  const docsService = useService(DocsService);
  const branchGraph = useBranchGraph();
  // Default expanded — branches are usually few (under 10) and the
  // researcher branched on purpose; hiding them by default would
  // make them invisible. The toggle is still here for clutter
  // control on heavy docs.
  const [open, setOpen] = useState(true);

  const branches = useMemo<BranchSummary[]>(() => {
    const entries = branchGraph.getBranches(docId);
    if (entries.length === 0) return [];
    return entries.map(entry => {
      // Read the latest title from the branch's DocRecord so renames
      // appear here without a reload.
      let title = 'Untitled branch';
      try {
        const record = docsService.list.doc$(entry.branchDocId).value;
        const metaTitle = record?.meta$.value.title?.trim();
        if (metaTitle) title = metaTitle;
      } catch {
        /* keep default */
      }
      return {
        branchDocId: entry.branchDocId,
        title,
        createdAt: entry.createdAt,
      };
    });
  }, [branchGraph, docId, docsService]);

  const onToggle = useCallback(() => setOpen(v => !v), []);

  // Hide the entire section when there are no branches — keeping it
  // visible would just add an empty "Branches · 0" row to every doc.
  if (branches.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.divider} />
      <Collapsible.Root open={open} onOpenChange={onToggle}>
        <Collapsible.Trigger className={styles.header} type="button">
          <span className={styles.headerLabel}>Branches</span>
          <span className={styles.headerCount}>· {branches.length}</span>
          <ToggleDownIcon
            className={styles.headerCaret}
            data-open={open ? 'true' : 'false'}
          />
        </Collapsible.Trigger>
        <Collapsible.Content>
          <ol className={styles.list}>
            {branches.map(branch => (
              <li key={branch.branchDocId}>
                <WorkbenchLink
                  to={`/${branch.branchDocId}`}
                  draggable={false}
                  className={styles.row}
                >
                  <span className={styles.rowTitle}>{branch.title}</span>
                  {branch.createdAt ? (
                    <span className={styles.rowDate}>
                      {formatRelativeDate(branch.createdAt)}
                    </span>
                  ) : null}
                </WorkbenchLink>
              </li>
            ))}
          </ol>
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
}

export function formatRelativeDate(iso: string): string {
  if (!iso) return '';
  try {
    const date = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  } catch {
    return '';
  }
}
